import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Chip,
  Avatar,
  Skeleton,
  Button,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Collapse,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import "react-quill/dist/quill.snow.css";

import { getProject } from "../../services/projectService";
import { getSprints } from "../../services/sprintService";
import { getTasks, createTask, updateTask, getTaskComments, createTaskComment } from "../../services/taskService";
import { useUserRole } from "../../context/UserRoleContext";
import CommentThread from "../common/CommentThread";
import TaskDialog from "./TaskDialog";

const TASK_TYPE_ICON = {
  Task: <TaskAltOutlinedIcon sx={{ fontSize: 16, color: "#2563eb" }} />,
  Bug: <BugReportOutlinedIcon sx={{ fontSize: 16, color: "#dc2626" }} />,
  Story: <AutoStoriesOutlinedIcon sx={{ fontSize: 16, color: "#16a34a" }} />,
};

const PRIORITY_ICON = {
  Low: <KeyboardDoubleArrowDownIcon sx={{ fontSize: 16, color: "#16a34a" }} />,
  Medium: <DragHandleIcon sx={{ fontSize: 16, color: "#ca8a04" }} />,
  High: <KeyboardDoubleArrowUpIcon sx={{ fontSize: 16, color: "#ea580c" }} />,
  Critical: <PriorityHighIcon sx={{ fontSize: 16, color: "#dc2626" }} />,
};

const priorityStyle = (priority) => {
  switch (priority) {
    case "Critical":
      return { background: "#fee2e2", color: "#dc2626" };
    case "High":
      return { background: "#ffedd5", color: "#ea580c" };
    case "Medium":
      return { background: "#fef3c7", color: "#ca8a04" };
    case "Low":
      return { background: "#dcfce7", color: "#16a34a" };
    default:
      return {};
  }
};

const statusStyle = (status) => {
  switch (status) {
    case "To Do":
      return { background: "#f1f5f9", color: "#475569" };
    case "In Progress":
      return { background: "#ede9fe", color: "#7c3aed" };
    case "Done":
      return { background: "#dcfce7", color: "#16a34a" };
    default:
      return {};
  }
};

// Dedicated full page for a single task (mirrors TicketDetails.jsx's layout),
// reached via /board/:projectId/task/:taskId.
function TaskDetails() {
  const { projectId, taskId } = useParams();
  const navigate = useNavigate();
  const { employee } = useUserRole();

  const [tasks, setTasks] = useState([]);
  const [projectTeam, setProjectTeam] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [moreFieldsOpen, setMoreFieldsOpen] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setNotFound(false);

      const [project, sprintData, taskData] = await Promise.all([
        getProject(projectId),
        getSprints(projectId),
        getTasks(projectId),
      ]);

      const team = Array.isArray(project.assigned_to) ? [...project.assigned_to] : [];
      if (project.project_manager_id && !team.some((m) => m.employee_id === project.project_manager_id)) {
        team.push({ employee_id: project.project_manager_id, employee_name: project.project_manager_name });
      }

      setProjectTeam(team);
      setSprints(sprintData);
      setTasks(taskData);

      if (!taskData.some((t) => String(t.task_id) === String(taskId))) {
        setNotFound(true);
      }
    } catch (error) {
      console.error("Task Details Load Error:", error.response?.data || error);
      setSnackbar({ open: true, message: "Failed to load task details", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [projectId, taskId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const task = tasks.find((t) => String(t.task_id) === String(taskId));
  const subtasks = task ? tasks.filter((t) => t.parent_task_id === task.task_id) : [];

  const handleCopyLink = () => {
    if (!task) return;
    const link = `${window.location.origin}/board/${projectId}/task/${task.task_id}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    });
  };

  const handleEditSave = async (data) => {
    if (!task || isSaving) return;

    try {
      setIsSaving(true);
      await updateTask(task.task_id, { ...data, parent_task_id: task.parent_task_id ?? null });
      await loadData();
      setEditOpen(false);
      setSnackbar({ open: true, message: "Task updated successfully", severity: "success" });
    } catch (error) {
      console.error("Task Update Error:", error.response?.data || error);
      setSnackbar({ open: true, message: error?.response?.data?.detail || "Failed to update task", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateSubtask = async (parentTask, title) => {
    try {
      await createTask({
        project_id: parentTask.project_id,
        sprint_id: parentTask.sprint_id,
        parent_task_id: parentTask.task_id,
        title,
        task_type: "Task",
        priority: "Medium",
        created_by: employee?.employee_id,
      });
      await loadData();
      setSnackbar({ open: true, message: "Subtask created successfully", severity: "success" });
    } catch (error) {
      console.error("Subtask Create Error:", error.response?.data || error);
      setSnackbar({ open: true, message: "Failed to create subtask", severity: "error" });
    }
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    return new Date(value).toLocaleString();
  };

  const formatDateOnly = (value) => {
    if (!value) return "N/A";
    return new Date(value).toLocaleDateString();
  };

  const detailsRows = task
    ? [
        {
          label: "Type",
          icon: TASK_TYPE_ICON[task.task_type] || <InfoOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Chip
              icon={TASK_TYPE_ICON[task.task_type]}
              label={task.task_type || "Task"}
              size="small"
              sx={{ background: "#f1f5f9", fontWeight: 600 }}
            />
          ),
        },
        {
          label: "Status",
          icon: <InfoOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Chip
              label={task.status || "N/A"}
              size="small"
              sx={{ fontWeight: 600, ...statusStyle(task.status) }}
            />
          ),
        },
        {
          label: "Priority",
          icon: <FlagOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Chip
              icon={PRIORITY_ICON[task.priority]}
              label={task.priority || "N/A"}
              size="small"
              sx={{ fontWeight: 600, ...priorityStyle(task.priority) }}
            />
          ),
        },
        {
          label: "Reporter",
          icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar sx={{ width: 26, height: 26, fontSize: 12, background: "#dbeafe", color: "#2563eb" }}>
                {task.created_by_name?.charAt(0)}
              </Avatar>
              <Typography fontSize={14} color="text.primary">{task.created_by_name || "N/A"}</Typography>
            </Box>
          ),
        },
        {
          label: "Assignee",
          icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar sx={{ width: 26, height: 26, fontSize: 12, background: "#dcfce7", color: "#16a34a" }}>
                {task.assigned_name?.charAt(0)}
              </Avatar>
              <Typography fontSize={14} color="text.primary">{task.assigned_name || "Unassigned"}</Typography>
            </Box>
          ),
        },
        {
          label: "Start Date",
          icon: <EventOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Typography fontSize={14} color="text.primary">
              {formatDateOnly(task.start_date)}
            </Typography>
          ),
        },
        {
          label: "Due Date",
          icon: <EventOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Typography fontSize={14} color="text.primary">
              {formatDateOnly(task.due_date)}
            </Typography>
          ),
        },
      ]
    : [];

  const moreFieldsRows = task
    ? [
        {
          label: "Assignee",
          icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar sx={{ width: 26, height: 26, fontSize: 12, background: "#dcfce7", color: "#16a34a" }}>
                {task.assigned_name?.charAt(0)}
              </Avatar>
              <Typography fontSize={14} color="text.primary">{task.assigned_name || "Unassigned"}</Typography>
            </Box>
          ),
        },
        {
          label: "Sprint",
          icon: <DirectionsRunOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Chip
              label={task.sprint_name || "Backlog"}
              size="small"
              sx={{ background: "#f1f5f9", fontWeight: 600 }}
            />
          ),
        },
        {
          label: "Story Points",
          icon: <InfoOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Typography fontSize={14} color="text.primary">
              {task.story_points ?? "N/A"}
            </Typography>
          ),
        },
        {
          label: "Created Date",
          icon: <EventOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Typography fontSize={14} color="text.primary">
              {formatDate(task.created_at)}
            </Typography>
          ),
        },
      ]
    : [];

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1.5}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/board/${projectId}`)}
          sx={{
            textTransform: "none",
            color: "#475569",
            fontWeight: 600,
            borderRadius: 2,
            "&:hover": { background: "#eef2f7" },
          }}
        >
          Back to Board
        </Button>

        {!loading && !notFound && task && (
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={() => setEditOpen(true)}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Edit Task
          </Button>
        )}
      </Box>

      {loading && (
        <>
          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              p: 3,
              mb: 3,
            }}
          >
            <Skeleton variant="text" width={120} height={16} sx={{ mb: 1 }} />
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Skeleton variant="rounded" width={70} height={28} />
              <Skeleton variant="text" width={260} height={32} />
              <Skeleton variant="rounded" width={70} height={24} />
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="rounded" width={70} height={24} />
            </Box>
            <Skeleton variant="text" width={200} height={18} sx={{ mt: 1.2 }} />
          </Box>

          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexDirection: { xs: "column", md: "row" } }}>
            {/* LEFT skeleton */}
            <Box
              sx={{
                flex: 2,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                p: 3,
                minWidth: 0,
              }}
            >
              <Box display="flex" alignItems="center" gap={0.8} mb={1.5}>
                <Skeleton variant="circular" width={18} height={18} />
                <Skeleton variant="text" width={100} height={18} />
              </Box>
              <Skeleton variant="rounded" height={140} />

              <Box sx={{ mt: 3 }}>
                <Skeleton variant="text" width={80} height={18} sx={{ mb: 1 }} />
                {[0, 1].map((i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.2, mb: 1.5 }}>
                    <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="30%" height={16} />
                      <Skeleton variant="text" width="90%" height={14} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* RIGHT skeleton */}
            <Box sx={{ flex: 1, minWidth: 260, display: "flex", flexDirection: "column", gap: 2.2 }}>
              <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2.5 }}>
                <Skeleton variant="text" width={70} height={16} sx={{ mb: 1.5 }} />
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Skeleton variant="text" width={60} height={14} sx={{ mb: 0.5 }} />
                    <Skeleton variant="rounded" width={100} height={22} />
                  </Box>
                ))}
              </Box>

              <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2.5 }}>
                <Skeleton variant="text" width={100} height={16} sx={{ mb: 1.5 }} />
                {[0, 1, 2].map((i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Skeleton variant="text" width={80} height={14} sx={{ mb: 0.5 }} />
                    <Skeleton variant="rounded" width={120} height={22} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </>
      )}

      {!loading && notFound && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            p: 6,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              background: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
            }}
          >
            <SearchOffRoundedIcon sx={{ fontSize: 48, color: "#94a3b8" }} />
          </Box>

          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1e293b", mb: 1 }}>
            Task Not Found
          </Typography>

          <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 3, maxWidth: 380 }}>
            This task doesn&apos;t exist or may have been deleted. Please check the link or go back to the board.
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate(`/board/${projectId}`)}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Back to Board
          </Button>
        </Box>
      )}

      {!loading && !notFound && task && (
        <>
          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              p: 3,
              mb: 3,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, mb: 1 }}>
              TASK OVERVIEW
            </Typography>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Tooltip title={linkCopied ? "Link copied!" : "Copy link"}>
                <Box
                  onClick={handleCopyLink}
                  sx={{
                    background: "#eff6ff",
                    color: "#2563eb",
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: "pointer",
                  }}
                >
                  #{task.task_id}
                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                </Box>
              </Tooltip>

              <Typography variant="h5" fontWeight={700} color="text.primary">
                {task.title}
              </Typography>

              <Chip
                icon={TASK_TYPE_ICON[task.task_type]}
                label={task.task_type || "Task"}
                size="small"
                sx={{ background: "#f1f5f9", fontWeight: 600 }}
              />

              <Chip
                label={task.status || "N/A"}
                size="small"
                sx={{ fontWeight: 600, ...statusStyle(task.status) }}
              />

              <Chip
                icon={PRIORITY_ICON[task.priority]}
                label={task.priority || "N/A"}
                size="small"
                sx={{ fontWeight: 600, ...priorityStyle(task.priority) }}
              />
            </Box>

            <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 1.2 }}>
              Created {formatDate(task.created_at)}
              {task.updated_at ? ` \u2022 Updated ${formatDate(task.updated_at)}` : ""}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexDirection: { xs: "column", md: "row" } }}>
            {/* LEFT: DESCRIPTION + SUBTASKS + ACTIVITY */}
            <Box
              sx={{
                flex: 2,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                p: 3,
                minWidth: 0,
              }}
            >
              <Box display="flex" alignItems="center" gap={0.8} mb={1.5}>
                <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#2563eb" }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>
                  Description
                </Typography>
              </Box>

              {task.description ? (
                <Box
                  className="ql-editor"
                  sx={{
                    p: 2,
                    fontSize: 14,
                    color: "text.primary",
                    lineHeight: 1.7,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    "&.ql-editor": { overflow: "visible" },
                    "& ul, & ol": { paddingLeft: "1.4em", margin: "0 0 8px" },
                    "& li": { listStyle: "revert", paddingLeft: 0 },
                    "& li::before": { content: "none" },
                    "& ul > li": { listStyleType: "disc" },
                    "& ol > li": { listStyleType: "decimal" },
                  }}
                  dangerouslySetInnerHTML={{ __html: task.description }}
                />
              ) : (
                <Typography sx={{ fontSize: 14, color: "#94a3b8", fontStyle: "italic" }}>
                  No description provided.
                </Typography>
              )}

              {subtasks.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 1.2 }}>
                    Subtasks ({subtasks.filter((s) => s.status === "Done").length}/{subtasks.length})
                  </Typography>

                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "background.default" }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Priority</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Assignee</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subtasks.map((subtask) => (
                          <TableRow
                            key={subtask.task_id}
                            hover
                            onClick={() => navigate(`/board/${projectId}/task/${subtask.task_id}`)}
                            sx={{ cursor: "pointer", "& td": { borderBottom: "1px solid", borderColor: "divider" } }}
                          >
                            <TableCell>
                              <Typography sx={{ fontSize: 13, color: "text.primary" }} noWrap>
                                {subtask.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={PRIORITY_ICON[subtask.priority]}
                                label={subtask.priority}
                                size="small"
                                sx={{ height: 20, fontSize: 10, fontWeight: 600, ...priorityStyle(subtask.priority) }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={subtask.status}
                                size="small"
                                sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.8}>
                                <Avatar sx={{ width: 22, height: 22, fontSize: 10 }}>
                                  {(subtask.assigned_name || "?").slice(0, 1).toUpperCase()}
                                </Avatar>
                                <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
                                  {subtask.assigned_name || "Unassigned"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                <CommentThread
                  entityId={task.task_id}
                  members={projectTeam}
                  currentEmployeeId={employee?.employee_id}
                  currentEmployeeName={employee?.employee_name}
                  fetchComments={getTaskComments}
                  postComment={createTaskComment}
                />
              </Box>
            </Box>

            {/* RIGHT: DETAILS + MORE FIELDS */}
            <Box sx={{ flex: 1, minWidth: 260, display: "flex", flexDirection: "column", gap: 2.2 }}>
              <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
                <Box
                  onClick={() => setDetailsOpen((prev) => !prev)}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.6, cursor: "pointer" }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Details
                  </Typography>
                  <IconButton size="small">
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                    />
                  </IconButton>
                </Box>

                <Collapse in={detailsOpen}>
                  <Box sx={{ px: 2.5, pb: 2 }}>
                    {detailsRows.map((row, index) => (
                      <React.Fragment key={row.label}>
                        <Box py={1.3}>
                          <Box display="flex" alignItems="center" gap={0.6} mb={0.7}>
                            {row.icon}
                            <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                              {row.label}
                            </Typography>
                          </Box>

                          {row.value}
                        </Box>

                        {index < detailsRows.length - 1 && <Divider sx={{ borderColor: "divider" }} />}
                      </React.Fragment>
                    ))}
                  </Box>
                </Collapse>
              </Box>

              <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
                <Box
                  onClick={() => setMoreFieldsOpen((prev) => !prev)}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.6, cursor: "pointer" }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    More Fields
                  </Typography>
                  <IconButton size="small">
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{ transform: moreFieldsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                    />
                  </IconButton>
                </Box>

                <Collapse in={moreFieldsOpen}>
                  <Box sx={{ px: 2.5, pb: 2 }}>
                    {moreFieldsRows.map((row, index) => (
                      <React.Fragment key={row.label}>
                        <Box py={1.3}>
                          <Box display="flex" alignItems="center" gap={0.6} mb={0.7}>
                            {row.icon}
                            <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                              {row.label}
                            </Typography>
                          </Box>

                          {row.value}
                        </Box>

                        {index < moreFieldsRows.length - 1 && <Divider sx={{ borderColor: "divider" }} />}
                      </React.Fragment>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {task && (
        <TaskDialog
          open={editOpen}
          task={task}
          projectTeam={projectTeam}
          sprints={sprints}
          allTasks={tasks}
          isSaving={isSaving}
          currentEmployeeName={employee?.employee_name}
          currentEmployeeId={employee?.employee_id}
          fullScreen={false}
          onClose={isSaving ? undefined : () => setEditOpen(false)}
          onSave={handleEditSave}
          onCreateSubtask={handleCreateSubtask}
          onEditSubtask={(subtask) => navigate(`/board/${projectId}/task/${subtask.task_id}`)}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default TaskDetails;
